import {
  MetricResult,
  MetricThresholdColor,
} from '@agilelab/plugin-wb-governance-common';
import {
  GenericField,
  useDefaultThresholdColors,
  useDrawersContext,
} from '@agilelab/plugin-wb-platform';
import { configApiRef, useApi } from '@backstage/core-plugin-api';

import {
  Box,
  createStyles,
  makeStyles,
  Theme,
  Typography,
  useTheme,
} from '@material-ui/core';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import FlagIcon from '@material-ui/icons/Flag';
import { useMemo } from 'react';
import { DetailDrawer } from './DetailDrawer/DetailDrawer';
import { TabEnum, useFlagAndScoreContext } from './FlagAndScoreContext';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    border: {
      borderTop: `1px solid ${theme.palette.grey[400]}`,
    },
    infoIcon: {
      color: theme.palette.info.main,
      marginRight: theme.spacing(1),
    },
    warningIcon: {
      color: theme.palette.warning.main,
      marginRight: theme.spacing(1),
    },
    errorIcon: {
      color: theme.palette.error.main,
      marginRight: theme.spacing(1),
    },
    title: {
      color: theme.palette.primary.main,
    },
  }),
);

export function FlagAndScore(): JSX.Element {
  const classes = useStyles();
  const theme = useTheme();

  const {
    setSelectedTab,
    resourceInfo,
    metricResults,
    policyViolationsCountMap,
  } = useFlagAndScoreContext();

  const { toggleBoolean } = useDrawersContext();

  const { defaultThresholdColors } = useDefaultThresholdColors();
  const configApi = useApi(configApiRef);
  const DEFAULT_COLOR = theme.palette.primary.main;
  const colors: MetricThresholdColor[] =
    configApi.getOptional('mesh.governance.metrics.thresholds.colors') ||
    defaultThresholdColors;

  const color = (labelColor: string) => {
    const relatedColor = colors.find(c => c.label === labelColor);

    return relatedColor?.value || DEFAULT_COLOR;
  };

  const violationsCounts = useMemo(() => {
    if (!policyViolationsCountMap) return undefined;

    const violationAggregated =
      policyViolationsCountMap.aggregatedViolations.get(
        resourceInfo.externalId,
      );
    return violationAggregated && violationAggregated?.size > 0
      ? Object.fromEntries(violationAggregated)
      : {};
  }, [policyViolationsCountMap, resourceInfo.externalId]);

  const showPolicyFlags =
    violationsCounts &&
    (violationsCounts.warning > 0 ||
      violationsCounts?.error > 0 ||
      violationsCounts?.info > 0);

  const metrics = useMemo(() => {
    return metricResults?.reduce((acc, curr) => {
      const isError = !!curr.evaluationResult.errors?.length;
      const key = isError
        ? 'Red'
        : curr.evaluationResult.thresholdResult?.labelColor ?? '';
      acc[key] = acc[key] || [];
      acc[key].push(curr);
      return acc;
    }, {} as { [key: string]: MetricResult[] });
  }, [metricResults]);

  return showPolicyFlags || metricResults?.length ? (
    <>
      <Box
        style={{
          borderBottom: `1px solid ${theme.palette.grey[200]}`,
          marginBottom: theme.spacing(2),
          marginTop: theme.spacing(2),
          width: '100%',
        }}
      />
      <Box display="flex" style={{ gap: 16 }}>
        {showPolicyFlags && (
          <GenericField
            label="Policies"
            value={
              <Box display="flex" style={{ gap: 8, minWidth: '250px' }}>
                {!!violationsCounts?.warning && (
                  <Box
                    style={{ cursor: 'pointer' }}
                    display="flex"
                    alignItems="center"
                    onClick={() => {
                      toggleBoolean(0);
                      setSelectedTab(TabEnum.Policies);
                    }}
                  >
                    <FlagIcon className={classes.warningIcon} />
                    <Typography className={classes.title}>
                      {violationsCounts.warning} Warning
                    </Typography>
                  </Box>
                )}
                {!!violationsCounts?.info && (
                  <Box
                    style={{ cursor: 'pointer' }}
                    display="flex"
                    alignItems="center"
                    onClick={() => {
                      toggleBoolean(0);
                      setSelectedTab(TabEnum.Policies);
                    }}
                  >
                    <FlagIcon className={classes.infoIcon} />
                    <Typography className={classes.title}>
                      {violationsCounts.info} Info
                    </Typography>
                  </Box>
                )}
                {!!violationsCounts?.error && (
                  <Box
                    display="flex"
                    alignItems="center"
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      toggleBoolean(0);
                      setSelectedTab(TabEnum.Policies);
                    }}
                  >
                    <FlagIcon className={classes.errorIcon} />
                    <Typography className={classes.title}>
                      {violationsCounts.error} Out of Policy
                    </Typography>
                  </Box>
                )}
              </Box>
            }
          />
        )}
        {!!metricResults?.length && (
          <GenericField
            label="Metrics"
            value={
              <Box display="flex" style={{ gap: 16, minWidth: '250px' }}>
                {metrics &&
                  Object.keys(metrics)?.map((key, index) => (
                    <Box
                      key={index}
                      display="flex"
                      alignItems="center"
                      style={{ cursor: 'pointer', gap: 4 }}
                      onClick={() => {
                        toggleBoolean(0);
                        setSelectedTab(TabEnum.Metrics);
                      }}
                    >
                      <FiberManualRecordIcon style={{ color: color(key) }} />
                      <Typography className={classes.title}>
                        {metrics[key]?.length}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            }
          />
        )}
      </Box>
      <DetailDrawer />
    </>
  ) : (
    <></>
  );
}
